import type { ComponentType } from 'react'

// Pure tour content + step building. The sidebar's own (permission-filtered)
// nav groups are the single input, so a tab the user cannot see (Billing for a
// role without billing access) never appears in the tour. One stop per sidebar
// group keeps the tour short (3 to 5 stops completes far better than 7+) while
// still explaining every tab.

export interface TourNavItem {
  name: string
  href: string
  icon?: ComponentType<{ className?: string }>
}
export interface TourNavGroup {
  label?: string
  items: TourNavItem[]
}

export interface TourStepItem {
  name: string
  href: string
  purpose: string
  icon?: ComponentType<{ className?: string }>
}
export interface TourStep {
  /** Matches `data-tour` on the sidebar group wrapper. */
  key: string
  eyebrow: string
  title: string
  items: TourStepItem[]
}

const GROUP_COPY: Record<string, { eyebrow: string; title: string }> = {
  home: { eyebrow: 'Home', title: 'Your home base' },
  build: { eyebrow: 'Build', title: 'Create what you send' },
  outreach: { eyebrow: 'Outreach', title: 'Reach and respond' },
  analyze: { eyebrow: 'Analyze', title: 'See what is working' },
  account: { eyebrow: 'Account', title: 'Plan, credits and usage' },
}

const PURPOSE: Record<string, string> = {
  '/dashboard': 'Setup progress, your next best step, and a live view of campaign performance.',
  '/workflows': 'Design multi-step email sequences on a visual canvas.',
  '/materials': 'Upload decks and docs the AI draws on to write relevant emails.',
  '/templates': 'Save reusable email structures so every send looks consistent.',
  '/contacts': 'Import leads, enrich them with AI research, and organise lists.',
  '/campaigns': 'Launch a workflow to your contacts and follow every send.',
  '/inbox': 'Replies in one place, sorted by sentiment with suggested answers.',
  '/tasks': 'Follow-ups that need a person, like LinkedIn actions.',
  '/analytics': 'Opens, replies and deliverability, by campaign and over time.',
  '/billing': 'Your plan, credit balance and usage history.',
}

/** Mirrors the server rule (app/services/product_tour.py): never offered, or snoozed once. */
export function shouldAutoOfferTour(state: string | null | undefined): boolean {
  return state == null || state === 'snoozed'
}

/** `data-tour` key for a sidebar group; the unlabeled first group is "home". */
export function tourKeyForGroup(label?: string): string {
  return label ? label.trim().toLowerCase() : 'home'
}

export function buildTourSteps(groups: TourNavGroup[]): TourStep[] {
  return groups
    .filter((g) => g.items.length > 0)
    .map((g) => {
      const key = tourKeyForGroup(g.label)
      const copy = GROUP_COPY[key] ?? { eyebrow: g.label ?? 'Tour', title: g.label ?? 'Around the app' }
      return {
        key,
        eyebrow: copy.eyebrow,
        title: copy.title,
        items: g.items.map((i) => ({
          name: i.name,
          href: i.href,
          icon: i.icon,
          purpose: PURPOSE[i.href] ?? `Open ${i.name}.`,
        })),
      }
    })
}

/** Where the card goes relative to the highlighted rect, clamped to the viewport. */
export interface Box {
  left: number
  top: number
  width: number
  height: number
}
export interface Placement {
  left: number
  top: number
  /** Arrow offset from the card top; null when the card sits under the target. */
  arrowTop: number | null
}

export function placeCard(
  target: Box,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
  opts: { gap?: number; margin?: number; pad?: number } = {},
): Placement {
  const gap = opts.gap ?? 18
  const margin = opts.margin ?? 12
  const pad = opts.pad ?? 8
  const right = target.left + target.width

  // Not enough room beside the target (narrow screens): sit below it instead.
  if (right + pad + gap + card.width > viewport.width - margin) {
    const left = Math.min(Math.max(margin, target.left), viewport.width - card.width - margin)
    const top = Math.min(target.top + target.height + pad + 12, viewport.height - card.height - margin)
    return { left, top: Math.max(margin, top), arrowTop: null }
  }

  const left = right + pad + gap
  const top = Math.min(Math.max(margin, target.top - pad), viewport.height - card.height - margin)
  const arrowY = target.top + target.height / 2 - top - 6
  return { left, top, arrowTop: Math.min(Math.max(18, arrowY), card.height - 30) }
}
